import { Button } from "antd";
import { Button as MantineCoreButton } from "@mantine/core";
import { Share } from "lucide-react";
import { VersionCounter } from "./VersionCounter";
import { useTopic } from "../hooks/use-topics";
import { MenuBar } from "components/Editor/MenuBar";
import { useOutputEditor } from "../hooks/use-output-editor";
import { Group, Modal, Stack } from "@mantine/core";
import { Grid, Header, Wrapper } from "../output-viewer.style";
import { useDisclosure } from "@mantine/hooks";
import {
  apiRequest,
  ProcessingCircle,
  quickTimer,
  useAccount,
  useDatadog
} from "@vojomo-core/shared";
import { useMemo, useState } from "react";
import GoogleDocsIcon from "../icons/googleDocs.svg";
import CopyToClipboardIcon from "../icons/copyToClipboard.svg";
import CheckMarkIcon from "../icons/checkMark.svg";
import VojomoIcon from "../icons/vojomoIcon.svg";
import { ShareButton } from "../share-button";
import { ShareAccessModal } from "./ShareAccessModal";
import { ActiveToggle } from "./ActiveToggle";
import { TopicConfiguration } from "../TopicConfiguration";

export const ToolBar = () => {
  const { vojomoAccount } = useAccount();
  const [copiedToClipboard, setCopiedToClipboard] = useState<boolean>(false);
  const [isShareAccessModalOpen, setIsShareAccessModalOpen] = useState(false);
  const [openedShareModal, { open: openShareModal, close: closeShareModal }] =
    useDisclosure(false);

  const { trackEvent } = useDatadog();

  const {
    topicType,
    deleteOutput,
    versionIndex,
    versionsCount,
    topic,
    output,
    currentOutputText,
    hasWriteAccess
  } = useTopic();

  const [openedDeleteModal, { close: closeDeleteModal }] = useDisclosure(false);
  const {
    editor,
    getExportContent,
    exportToGoogleDocs,
    isExporting,
    pendingGoogleDocId,
    setPendingGoogleDocId
  } = useOutputEditor();

  const shareButtons = useMemo(() => {
    const arr = [
      {
        icon: GoogleDocsIcon,
        label: "Google Docs",
        onClick: async () => {
          trackEvent("exporting_to_google_docs", {
            topicId: topic["_id"],
            outputId: output["_id"]
          });
          await exportToGoogleDocs();
        }
      },
      {
        icon: copiedToClipboard ? CheckMarkIcon : CopyToClipboardIcon,
        label: "Copy To Clipboard",
        onClick: async () => {
          trackEvent("copied_to_clipboard", {
            topicId: topic["_id"],
            outputId: output["_id"]
          });

          const htmlBlobArray = getExportContent();

          if (htmlBlobArray.length > 0) {
            const data = [
              new ClipboardItem({
                ["text/html"]: new Blob(htmlBlobArray, {
                  type: "text/html"
                }),

                ["text/plain"]: new Blob([currentOutputText], {
                  type: "text/plain"
                })
              })
            ];

            await navigator.clipboard.write(data);
            setCopiedToClipboard(true);

            await quickTimer(1000);
            setCopiedToClipboard(false);
          }
        }
      }
    ];

    if (
      topic.owner === vojomoAccount["_id"] &&
      topicType !== "building-block" &&
      topicType !== "briefing-doc" &&
      hasWriteAccess
    ) {
      arr.push({
        icon: VojomoIcon,
        label: "Share Access",
        onClick: async () => setIsShareAccessModalOpen(true)
      });
    }

    return arr;
  }, [topicType, topic, vojomoAccount, currentOutputText, copiedToClipboard]);

  return (
    <div className="doc-toolbar">
      <div className="doc-toolbar-left">
        {topicType === "building-block" && topic.is_team === false && (
          <ActiveToggle
            disabled={!hasWriteAccess}
            initialValue={topic.active}
            onLabel={"Contributed To Team"}
            offLabel={"Not Contributed To Team"}
            onToggle={async (val) => {
              await apiRequest({
                subRoute: "topic",
                path: `/update_topic`,
                method: "POST",
                body: {
                  payload: { active: val },
                  topicId: topic["_id"]
                }
              });
            }}
          />
        )}
        {topicType !== "building-block" && topicType !== "briefing-doc" && (
          <>
            <div className="doc-type">
              <TopicConfiguration disabled={!hasWriteAccess} />
            </div>
          </>
        )}
        {hasWriteAccess && (
          <>
            <div className="divider" />
            <div className="doc-toolbar-icons">
              <MenuBar editor={editor} />
            </div>
          </>
        )}
      </div>
      <div className="doc-toolbar-right">
        <VersionCounter />
        <div className="doc-toolbar-actions">
          {/* <Button type="text" size="small" className="icon-button-muted" onClick={() => }>
            <Trash2 className="icon-14" />
          </Button> */}
          <Button
            type="text"
            size="small"
            className="icon-button-muted"
            onClick={() => {
              openShareModal();
            }}
          >
            <Share className="icon-14" />
          </Button>
        </div>
      </div>
      <Modal opened={openedDeleteModal} onClose={closeDeleteModal}>
        <Wrapper>
          <Header>
            Are you sure you want to{" "}
            {versionIndex == versionsCount - 1 ? "delete" : "restore to"} this
            version?
          </Header>
          <Group mt="md">
            <Button
              onClick={async () => {
                await deleteOutput();
                closeDeleteModal();
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                closeDeleteModal();
              }}
            >
              No
            </Button>
          </Group>
        </Wrapper>
      </Modal>
      {isShareAccessModalOpen && (
        <ShareAccessModal
          refId={topic["_id"]}
          refType="topic"
          onClose={() => setIsShareAccessModalOpen(false)}
        />
      )}

      <Modal
        opened={openedShareModal}
        onClose={closeShareModal}
        centered
        withCloseButton={false}
      >
        <Wrapper>
          <Header>Share your output to:</Header>
          <br />
          <Grid>
            {shareButtons.map((button, index) => (
              <ShareButton
                key={index}
                icon={button.icon}
                label={button.label}
                onClick={async () => {
                  await button.onClick();
                  closeShareModal();
                }}
              />
            ))}
          </Grid>
        </Wrapper>
      </Modal>
      <Modal
        opened={isExporting}
        onClose={null}
        centered
        withCloseButton={false}
        closeOnClickOutside={false}
      >
        <ProcessingCircle text="Exporting" />
      </Modal>
      <Modal
        opened={!!pendingGoogleDocId}
        onClose={() => setPendingGoogleDocId(null)}
        centered
        withCloseButton
        closeOnClickOutside
      >
        <Wrapper>
          <Header>Document successfully exported to Google Docs</Header>
          <Stack mt="md" align="center" w="100%">
            <MantineCoreButton
              w="70%"
              fullWidth
              color="blue"
              onClick={() =>
                window.open(
                  `https://docs.google.com/document/d/${pendingGoogleDocId}/edit`,
                  "_blank"
                )
              }
            >
              Open in Google Docs
            </MantineCoreButton>

            <MantineCoreButton
              w="70%"
              fullWidth
              variant="outline"
              color="blue"
              onClick={() => setPendingGoogleDocId(null)}
            >
              Close
            </MantineCoreButton>
          </Stack>
        </Wrapper>
      </Modal>
    </div>
  );
};
